<template>
  <div class="register-search-view">
    <div
      v-if="isLoading"
      class="loading"
    >
      <unc-loader />
    </div>

    <div
      v-else
      class="register-card"
    >
      <div class="register-glow" />

      <div class="card-header ch-alt register-header">
        <div class="register-kicker rr-kicker--info">
          Paso 1 de 4
        </div>
        <h1 class="register-title">
          Empecemos
        </h1>
        <p class="register-subtitle">
          Vamos a guiarte en la creación de tu Usuario UNC.
        </p>
      </div>

      <div class="card-body register-body">
        <form
          class="register-form"
          @submit.prevent="validateBeforeSubmit"
        >
          <div
            class="form-group register-field"
            :class="{ 'has-error': errors.has('countrySelected') }"
          >
            <div>
              <label for="countryCode">País</label>
              <div class="register-select">
                <select
                  id="countryCode"
                  v-model="item.countrySelected"
                  v-validate="'required'"
                  name="countrySelected"
                  class="form-control"
                  :aria-describedby="errors.has('countrySelected') ? 'countryCode-error' : null"
                >
                  <option value="">
                    [Seleccione una opción]
                  </option>
                  <option
                    v-for="country of optionsCountries"
                    :key="country.code_iso_alfa3"
                    :value="country"
                  >
                    {{ country.name_iso }}
                  </option>
                </select>
              </div>
            </div>
            <small
              v-if="errors.has('countrySelected')"
              id="countryCode-error"
              class="help-block"
              role="alert"
            >{{ errors.first('countrySelected') }}</small>
          </div>

          <div
            class="form-group register-field"
            :class="{ 'has-error': errors.has('identityTypeId') }"
          >
            <div>
              <label for="identityTypeId">Tipo de documento</label>
              <div class="register-select">
                <select
                  id="identityTypeId"
                  v-model="item.code"
                  v-validate="!isChangingCountry ? 'required' : ''"
                  name="identityTypeId"
                  class="form-control"
                  :aria-describedby="errors.has('identityTypeId') ? 'identityTypeId-error' : null"
                >
                  <option value="">
                    [Seleccioná una opción]
                  </option>
                  <option
                    v-for="opt of optionsIdentityTypes"
                    :key="opt.id"
                    :value="opt.id"
                  >
                    {{ opt.short_name + ' - ' + opt.name }}
                  </option>
                </select>
              </div>
            </div>
            <small
              v-if="errors.has('identityTypeId')"
              id="identityTypeId-error"
              class="help-block"
              role="alert"
            >{{ errors.first('identityTypeId') }}</small>
          </div>

          <div
            class="form-group register-field"
            :class="{ 'has-error': errors.has('item.identifier') }"
          >
            <div>
              <label for="item.identifier">Número de documento</label>
              <input
                id="item.identifier"
                v-model="item.identifier"
                v-validate="selectedIdentityValidation"
                name="item.identifier"
                type="text"
                class="form-control"
                placeholder="Ingresá tu número de documento..."
                :aria-describedby="errors.has('item.identifier') ? 'identifier-error' : null"
              >
            </div>
            <small
              v-if="errors.has('item.identifier')"
              id="identifier-error"
              class="help-block"
              role="alert"
            >{{ errors.first('item.identifier') }}</small>
          </div>

          <div class="row register-actions-row">
            <div class="col-md-12 text-right register-actions">
              <button
                type="button"
                class="btn btn-link waves-effect register-btn register-btn-secondary"
                @click.prevent="goBack()"
              >
                Salir
              </button>
              <button
                type="submit"
                class="btn btn-primary waves-effect register-btn register-btn-primary"
              >
                <span>Siguiente</span>
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { HttpFRONT, swal } from '../../share.js';
import { mapGetters, mapActions } from 'vuex';
import withoutSpaces from '../../../utils/withoutSpaces';
import validateForm from '../../mixins/validate-form';

export default {
    name: 'RegisterSearch',
    mixins: [validateForm],
    data () {
        return {
            isInitialized: false,
            isLoading: true,
            isChangingCountry: false,
            item: {
                identifier: '',
                code: '',
                countryCode: '',
                countrySelected: '',
            },
            optionsCountries: [],
            optionsIdentityTypes: [],
        };
    },
    computed: {
        ...mapGetters([
            'sharedComponentsInfo',
        ]),
        selectedIdentityValidation () {
            const selectedType = this.optionsIdentityTypes.find(type => type.id === this.item.code);
            if (!selectedType) {
                return 'required';
            }
            if (selectedType.code === 'ARG_CUIL') {
                return 'required|min:11|max:13|cuilFormat|cuilNumber';
            }
            if (selectedType.code === 'ARG_DNI') {
                return 'required|min:7|max:8|dni';
            }
            return 'required|max:30';
        },
    },
    watch: {
        async 'item.countrySelected' (country) {
            if (!this.isInitialized) return;

            this.isChangingCountry = true;

            this.item.code = '';

            this.$validator.reset('identityTypeId');

            try {
                if (country) {
                    await this.getIdentityTypesForCountry(country);
                } else {
                    this.optionsIdentityTypes = [];
                }
            } finally {
                this.isChangingCountry = false;
            }
        },
    },
    async mounted () {
        await this.getData();
        if (this.sharedComponentsInfo.countryCode || this.sharedComponentsInfo.identityType || this.sharedComponentsInfo.identityNumber) {
            this.isInitialized = false;

            if (this.sharedComponentsInfo.countryCode) {
                const sharedCountry = this.optionsCountries.find(country => country.code_iso_alfa3 === this.sharedComponentsInfo.countryCode);
                if (sharedCountry) {
                    this.item.countryCode = this.sharedComponentsInfo.countryCode;
                    this.item.countrySelected = sharedCountry;
                    await this.getIdentityTypesForCountry(sharedCountry);
                }
            }

            if (this.sharedComponentsInfo.identityType) {
                const identityType = this.optionsIdentityTypes.find(type => type.code === this.sharedComponentsInfo.identityType);
                if (identityType) {
                    this.item.code = identityType.id;
                }
            }

            if (this.sharedComponentsInfo.identityNumber) {
                this.item.identifier = this.sharedComponentsInfo.identityNumber;
            }

            this.isInitialized = true;
        }
    },
    methods: {
        ...mapActions([
            'getCountries',
            'getIdentityTypes',
        ]),
        async getData () {
            try {
                this.optionsCountries = await this.getCountries();
                this.item.countrySelected = this.optionsCountries.find(country => country.name_iso === 'Argentina');
                await this.getIdentityTypesForCountry(this.item.countrySelected);
                const defaultType = this.optionsIdentityTypes.find(type => type.code === 'ARG_DNI');
                if (defaultType) {
                    this.item.code = defaultType.id;
                }
            } catch (error) {
                swal({ title: 'Algo salió mal', text: 'No pudimos cargar la página. Por favor, intentá nuevamente más tarde.', type: 'warning' });
                this.$router.push('/');
            } finally {
                this.isInitialized = true;
                this.isLoading = false;
            }
        },
        async getIdentityTypesForCountry (countrySelected) {
            this.optionsIdentityTypes = await this.getIdentityTypes(countrySelected.code_iso_alfa3);

            if (!this.optionsIdentityTypes.find(option => option.code === 'WORLD_PASS')) {
                this.optionsIdentityTypes.push({ id: 1, code: 'WORLD_PASS', name: 'Pasaporte', short_name: 'PASS' });
            }
        },
        async getPeopleByIdentifier () {
            try {
                const dataIdentityType = this.optionsIdentityTypes.find(
                    option => option.id === this.item.code,
                );

                if (this.sharedComponentsInfo?.countryCode === this.item.countrySelected.code_iso_alfa3 &&
                    this.sharedComponentsInfo?.identityType === dataIdentityType.code &&
                    this.sharedComponentsInfo?.identityNumber === withoutSpaces(this.item.identifier)) {
                    this.$store.commit('setCurrentComponent', 'register-search-results');
                    return;
                }

                this.$store.commit('setShowLoading', true);
                this.$store.commit('setSearchResult', null);

                const searchIdentifier = {
                    _id: dataIdentityType.id,
                    code: dataIdentityType.code,
                    name: dataIdentityType.name,
                    countryCode: this.item.countrySelected.code_iso_alfa3,
                    countryName: this.item.countrySelected.name_iso,
                    number: withoutSpaces(this.item.identifier),
                };
                this.$store.commit('setSearchIdentifier', searchIdentifier);

                const { status, data } = await HttpFRONT.get(
                    `/usersData/${searchIdentifier.number}?identityTypeId=${searchIdentifier.code}`,
                );

                if (status === 200) {
                    this.$store.commit('setSearchResult', data);
                    this.$store.commit('setCurrentComponent', 'register-search-results');
                }
            } catch (error) {
                const status = error?.response?.status;

                if (status === 403) {
                    swal({
                        title: 'Acceso denegado',
                        text: 'No tenés permisos para acceder a este servicio.',
                        type: 'warning',
                    });
                } else if (status === 400) {
                    swal({
                        title: 'Solicitud incorrecta',
                        text: 'Datos de búsqueda inválidos',
                        type: 'warning',
                    });
                } else {
                    swal({
                        title: 'Algo salió mal',
                        text: 'Servicio temporalmente no disponible.',
                        type: 'warning',
                    });
                }
            } finally {
                this.$store.commit('setShowLoading', false);
            }
        },
        async validateBeforeSubmit () {
            try {
                if (await this.validateForm()) {
                    await this.getPeopleByIdentifier();
                }
            } catch (error) {
                console.error('Error al validar el formulario:', error);
                swal({ title: 'Algo salió mal', text: 'No pudimos recuperar los datos.', type: 'warning' });
            }
        },
        goBack () {
            this.$store.dispatch('clearSharedComponentsInfo');
            this.$router.push('/');
        },
    },
};
</script>

<style lang="scss" scoped>
@import './register-shared.scss';

.register-search-view {
  --register-azul: #003865;
  --register-azul-deep: #001f3f;
  --register-azul-mid: #0a5f96;
  --register-verde: #3eb6b8;
  --register-ink: #102238;
  --register-muted: #526579;
  --register-border: rgba(0, 56, 101, 0.14);

  position: relative;
  padding: 0;
}

.register-form {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  column-gap: 18px;
  row-gap: 8px;
}

.register-field:nth-of-type(3) {
  grid-column: 1 / -1;
}

.register-actions-row {
  grid-column: 1 / -1;
  margin-top: 2px;
}

.register-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 991px) {
  .register-search-view {
    border-radius: 20px;
  }

  .register-body {
    padding-left: 22px;
    padding-right: 22px;
  }

  .register-form {
    grid-template-columns: 1fr;
  }

  .register-field:nth-of-type(3) {
    grid-column: auto;
  }

  .register-actions {
    justify-content: stretch;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .register-btn {
    width: 100%;
  }
}
</style>
